import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Inbox, Search, Phone, Mail, MessageSquareText, AlertTriangle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminContact() {
  const [data, setData] = useState({ total: 0, submissions: [] });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/contact-submissions")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return data.submissions;
    return data.submissions.filter((r) =>
      `${r.name} ${r.contact_no} ${r.email || ""} ${r.comment}`.toLowerCase().includes(t)
    );
  }, [data.submissions, search]);

  const sentCount = data.submissions.filter((r) => r.email_sent).length;
  const failedCount = data.submissions.filter((r) => !r.email_sent && r.email_error).length;

  return (
    <DashboardShell>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Inbox className="w-6 h-6 text-sg-gold-deep" />
          <div className="overline text-sg-gold-deep">CONTACT INBOX</div>
        </div>
        <h1 className="font-display text-4xl text-sg-navy">Enquiries from the Contact Us page</h1>
        <p className="mt-2 text-sg-mute">
          Every submission is saved here, whether or not the Resend email delivery succeeded.
          {failedCount > 0 && (
            <span className="ml-2 text-amber-700 inline-flex items-center gap-1" data-testid="admin-contact-warn">
              <AlertTriangle className="w-3.5 h-3.5" /> {failedCount} submission{failedCount === 1 ? "" : "s"} didn&apos;t send an email — check RESEND_API_KEY.
            </span>
          )}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
          <StatTile label="TOTAL" value={data.total} testid="admin-contact-total" />
          <StatTile label="EMAIL SENT" value={sentCount} testid="admin-contact-sent" tone="emerald" />
          <StatTile label="EMAIL FAILED" value={failedCount} testid="admin-contact-failed" tone={failedCount ? "amber" : "muted"} />
          <StatTile label="LAST 24 H" value={data.submissions.filter((r) => (Date.now() - new Date(r.created_at).getTime()) < 86400000).length} testid="admin-contact-recent" />
        </div>

        <div className="mt-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sg-mute pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name · phone · email · text…"
            className="pl-9 h-10 rounded-full bg-white border-sg-line"
            data-testid="admin-contact-search"
          />
        </div>

        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="text-sm text-sg-mute py-12 text-center">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="text-sm text-sg-mute py-16 text-center border border-dashed border-sg-line rounded-3xl">
              {data.total === 0 ? "No enquiries yet." : "No submissions match your search."}
            </div>
          ) : (
            visible.map((r) => (
              <div key={r.submission_id} className="bg-white border border-sg-line rounded-2xl p-5" data-testid={`admin-contact-row-${r.submission_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl text-sg-navy">{r.name}</div>
                    <div className="mt-1 text-sm text-sg-mute flex items-center gap-3 flex-wrap">
                      <a href={`tel:+${r.contact_no.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1 hover:text-sg-navy"><Phone className="w-3.5 h-3.5" /> {r.contact_no}</a>
                      {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-sg-navy"><Mail className="w-3.5 h-3.5" /> {r.email}</a>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.email_sent ? (
                      <Badge className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">email sent</Badge>
                    ) : (
                      <Badge className="rounded-full bg-amber-100 text-amber-800 border border-amber-200" title={r.email_error || ""}>email pending</Badge>
                    )}
                    {r.email && (
                      r.autoreply_sent ? (
                        <Badge className="rounded-full bg-sg-cream-soft text-sg-navy border border-sg-gold/40" title="Auto-reply confirmation sent to visitor">auto-reply sent</Badge>
                      ) : (
                        <Badge className="rounded-full bg-sg-cream-soft text-sg-mute border border-sg-line" title={r.autoreply_error || ""}>auto-reply pending</Badge>
                      )
                    )}
                    <span className="text-xs text-sg-mute font-mono">{timeAgo(r.created_at)}</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-sg-navy whitespace-pre-wrap bg-sg-cream-soft border border-sg-line rounded-2xl p-4 flex gap-3">
                  <MessageSquareText className="w-4 h-4 text-sg-gold-deep shrink-0 mt-0.5" />
                  <span>{r.comment}</span>
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-sg-mute/60 font-mono">
                  {r.submission_id} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function StatTile({ label, value, testid, tone = "neutral" }) {
  const cls =
    tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "muted" ? "bg-sg-cream-soft border-sg-line text-sg-mute"
    : "bg-white border-sg-line text-sg-navy";
  return (
    <div className={`rounded-2xl p-4 border ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] opacity-80">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}
